import React, { useEffect, useState } from 'react';
import { Table, Divider, DOMHelper } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ImageCell from '@/components/ImageCell';
import PageContent from '@/components/PageContent';
import { AdvanceShareTemplateDesign, AdvanceShareTemplatePaginationFilterRequest } from '@/models/advance-share-template.model';
import { getPagedAdvanceShareTemplateDesigns } from '@/services/advance-share-template.service';
import { AdvanceShareStyleAiAspectRatioList } from '@/models/advance-share-style-definition.model';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface AdvanceShareTemplateDesignListProps {
  templateId: number;
}

const AdvanceShareTemplateDesignList: React.FC<AdvanceShareTemplateDesignListProps> = ({ templateId }) => {
  const [filterReq, setFilterReq] = useState<AdvanceShareTemplatePaginationFilterRequest>(new PaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<AdvanceShareTemplateDesign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: AdvanceShareTemplatePaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<AdvanceShareTemplateDesign> = await getPagedAdvanceShareTemplateDesigns(templateId, req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching template designs:${errorModel?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContent>
      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        loading={loading}
        rowHeight={80}
      >
        <Column flexGrow={1} align="center">
          <HeaderCell>Cover Image</HeaderCell>
          <ImageCell dataKey="coverImageUrl" />
        </Column>
        <Column flexGrow={1} align="center">
          <HeaderCell>Aspect Ratio</HeaderCell>
          <Cell>
            {(rowData: AdvanceShareTemplateDesign) => (AdvanceShareStyleAiAspectRatioList.findLast(s => s.value == rowData.aspectRatio)?.label)}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({ ...pre, pageNumber: dataKey }))}
        onChangeLimit={dataKey => setFilterReq(pre => ({ ...pre, pageSize: dataKey }))}
      />
    </PageContent>
  );
};

export default AdvanceShareTemplateDesignList;
